import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './EmailLogin.css'

interface EmailLoginProps {
    onEmailSent?: () => void
}

const EmailLogin: React.FC<EmailLoginProps> = ({ onEmailSent }) => {
    const [email, setEmail] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [devMagicLink, setDevMagicLink] = useState('')
    const navigate = useNavigate()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address')
            return
        }

        setLoading(true)

        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000'
            const response = await fetch(`${apiUrl}/partners/request-access`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email: email.toLowerCase().trim() })
            })

            const data = await response.json()

            if (response.ok && data.success) {
                // Check if we're in development mode and got a token
                if (data.data?.devToken) {
                    // Development mode - show magic link directly
                    const magicLink = `${window.location.origin}/auth?token=${data.data.devToken}`
                    setDevMagicLink(magicLink)
                } else {
                    // Production mode - redirect to "email sent" screen
                    if (onEmailSent) {
                        onEmailSent()
                    }
                    navigate('/sent', { state: { email } })
                }
            } else {
                setError(data.message || 'Failed to send magic link. Please try again.')
            }
        } catch (err) {
            console.error('Error requesting access:', err)
            setError('Connection error. Please try again later.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="email-login-container">
            <div className="email-login-card">
                <div className="email-login-header">
                    <h1>EmpreX API</h1>
                    <p className="subtitle">Partner Documentation Portal</p>
                </div>

                <div className="email-login-body">
                    <h2>Access Documentation</h2>
                    <p className="description">
                        Enter your registered email to receive a magic link for instant access
                    </p>

                    <form onSubmit={handleSubmit}>
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <input
                                id="email"
                                type="email"
                                placeholder="your@company.com"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                disabled={loading}
                                required
                                autoFocus
                            />
                        </div>

                        <button
                            type="submit"
                            className="submit-button"
                            disabled={loading || !email}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    Sending...
                                </>
                            ) : (
                                'Send Magic Link'
                            )}
                        </button>
                    </form>

                    {devMagicLink ? (
                        <div className="dev-magic-link">
                            <div className="dev-badge">
                                🔧 Development Mode
                            </div>
                            <p className="dev-message">
                                Email not configured. Use the link below to test:
                            </p>
                            <a
                                href={devMagicLink}
                                className="magic-link-button"
                            >
                                🔗 Click here to login
                            </a>
                            <button
                                onClick={() => {
                                    setDevMagicLink('')
                                    setEmail('')
                                }}
                                className="back-button-small"
                            >
                                ← Back
                            </button>
                        </div>
                    ) : (
                        <div className="info-note">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zM8 4a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 1 0v-3A.5.5 0 0 0 8 4zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
                            </svg>
                            <span>No password required. We'll send you a secure link via email.</span>
                        </div>
                    )}
                </div>

                <div className="email-login-footer">
                    <p>© {new Date().getFullYear()} EmpreX. All rights reserved.</p>
                </div>
            </div>
        </div>
    )
}

export default EmailLogin
